import { Fragment, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SnoozeIcon from '@mui/icons-material/Snooze';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import paths from '@src/paths';

const PRIORITY_LABEL = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const SORT_FIELD_OPTIONS = [
  { value: 'created_at', label: 'Created date' },
  { value: 'due_date', label: 'Due date' },
];

const SORT_DIRECTION_OPTIONS = [
  { value: 'asc', label: 'Oldest first' },
  { value: 'desc', label: 'Newest first' },
];

const SOURCE_BADGE = {
  inbox: { label: 'Inbox', color: 'secondary' },
  budget: { label: 'Budget', color: 'success' },
  pacing: { label: 'Pacing', color: 'warning' },
  zip: { label: 'ZIP', color: 'info' },
  'fb-push': { label: 'Push to FB', color: 'primary' },
  meeting: { label: 'Meeting', color: 'warning' },
};

function taskStateColor(task) {
  if (task.is_completed) return { color: 'success.main', label: 'Done' };
  if (task.snoozed_until && dayjs(task.snoozed_until).isAfter(dayjs())) {
    return { color: 'warning.main', label: `Snoozed until ${dayjs(task.snoozed_until).format('MMM D')}` };
  }
  return { color: 'grey.500', label: 'Open' };
}

function SnoozeMenu({ task, onSnooze }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const isSnoozed = Boolean(
    task.snoozed_until && dayjs(task.snoozed_until).isAfter(dayjs()),
  );

  const handleSnooze = (delta) => {
    handleClose();
    if (delta === null) {
      onSnooze(task, null);
      return;
    }
    const until = dayjs().add(delta.amount, delta.unit).hour(9).minute(0).second(0).toISOString();
    onSnooze(task, until);
  };

  const handleCustom = () => {
    handleClose();
    const input = window.prompt(
      'Snooze until when? (YYYY-MM-DD or YYYY-MM-DD HH:mm)',
      dayjs().add(1, 'week').format('YYYY-MM-DD'),
    );
    if (!input) return;
    const parsed = dayjs(input);
    if (!parsed.isValid()) {
      window.alert('Invalid date. Use YYYY-MM-DD or YYYY-MM-DD HH:mm.');
      return;
    }
    onSnooze(task, parsed.toISOString());
  };

  return (
    <>
      <Tooltip title={isSnoozed ? 'Snoozed — change or wake up' : 'Snooze'}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color: isSnoozed ? 'warning.main' : 'text.secondary' }}
        >
          <SnoozeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => handleSnooze({ amount: 1, unit: 'day' })}>Tomorrow 9am</MenuItem>
        <MenuItem onClick={() => handleSnooze({ amount: 3, unit: 'day' })}>In 3 days</MenuItem>
        <MenuItem onClick={() => {
          handleClose();
          // Next Monday 9am
          const today = dayjs();
          const daysUntilMonday = (8 - today.day()) % 7 || 7;
          const until = today.add(daysUntilMonday, 'day').hour(9).minute(0).second(0).toISOString();
          onSnooze(task, until);
        }}>Next Monday</MenuItem>
        <MenuItem onClick={() => handleSnooze({ amount: 2, unit: 'week' })}>In 2 weeks</MenuItem>
        <MenuItem onClick={handleCustom}>Custom date…</MenuItem>
        {isSnoozed && [
          <Divider key="divider" />,
          <MenuItem key="wake" onClick={() => handleSnooze(null)}>Wake up now</MenuItem>,
        ]}
      </Menu>
    </>
  );
}

SnoozeMenu.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number,
    snoozed_until: PropTypes.string,
  }).isRequired,
  onSnooze: PropTypes.func.isRequired,
};

function DetailBlock({ label, children }) {
  if (!children) {
    return null;
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Box>
  );
}

DetailBlock.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

DetailBlock.defaultProps = {
  children: null,
};

/**
 * Compute a deep-link target for a task based on its source.
 * Returns { to, label } or null if no link is available.
 *
 * Source-key shapes used here:
 *   inbox-thread:<thread_id>
 *   budget-period:<client_id>:<period_iso>
 *   budget-adjustment:<client_id>:<period_iso>:<adjustment_id>
 *   budget-pacing:<client_id>:<period_iso>           (follow-up after budget approval)
 *   budget-pacing:turnoff:<adjustment_id>            (follow-up after turn-off approval)
 *   zip-submission:<id>           (no client_id — fall back to task.client)
 *   zip-fb-push:<submission_id>   (follow-up after ZIP approval)
 */
function deepLinkForTask(task) {
  const key = task.source_key || '';

  if (key.startsWith('inbox-thread:')) {
    const threadId = key.split(':', 2)[1];
    if (threadId) {
      return { to: `${paths.clientInbox}?thread=${threadId}`, label: 'Open thread' };
    }
  }

  if (key.startsWith('budget-period:') || key.startsWith('budget-adjustment:')) {
    const parts = key.split(':');
    const clientId = parts[1];
    const period = parts[2];
    if (clientId && period) {
      return {
        to: `${paths.budgetCenter}?client_id=${clientId}&period=${period}`,
        label: 'Open budget',
      };
    }
  }

  if (key.startsWith('budget-pacing:') && task.client) {
    return {
      to: `${paths.facebook.budgetOptimizer}?client_id=${task.client}`,
      label: 'Open Budget Optimizer',
    };
  }

  if (key.startsWith('zip-submission:') && task.client) {
    return {
      to: `${paths.zipSubmissions}?client_id=${task.client}`,
      label: 'Open ZIP review',
    };
  }

  if (key.startsWith('zip-fb-push:') && task.client) {
    return {
      to: `${paths.facebook.campaignBuilder}?client_id=${task.client}`,
      label: 'Open Campaign Builder',
    };
  }

  return null;
}

function ProposalCard({ task, onApprove, onReject }) {
  const [working, setWorking] = useState(false);

  const handleApprove = () => {
    setWorking(true);
    Promise.resolve(onApprove(task)).finally(() => setWorking(false));
  };
  const handleReject = () => {
    const reason = window.prompt('Reject reason (optional):', '') || '';
    setWorking(true);
    Promise.resolve(onReject(task, reason)).finally(() => setWorking(false));
  };

  const actions = Array.isArray(task.ai_proposed_actions) ? task.ai_proposed_actions : [];
  const hasReply = Boolean((task.ai_proposed_reply || '').trim());
  const confidenceColor = (n) => {
    if (n == null) return 'default';
    if (n >= 85) return 'success';
    if (n >= 60) return 'primary';
    return 'warning';
  };

  return (
    <Box sx={{ px: 2, py: 2.5, backgroundColor: (theme) => theme.palette.action.hover }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Chip size="small" label="AI proposal" color="primary" />
        {task.ai_confidence != null ? (
          <Chip
            size="small"
            label={`${task.ai_confidence}% confidence`}
            color={confidenceColor(task.ai_confidence)}
            variant="outlined"
          />
        ) : null}
        {task.ai_model ? (
          <Typography variant="caption" color="text.secondary">{task.ai_model}</Typography>
        ) : null}
      </Stack>

      {task.ai_reasoning ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
            AI reasoning
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {task.ai_reasoning}
          </Typography>
        </Box>
      ) : null}

      {actions.length ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
            Proposed action{actions.length === 1 ? '' : 's'}
          </Typography>
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {actions.map((a, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  {a.type || 'action'}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.25 }}>
                  {a.summary || JSON.stringify(a)}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : null}

      {hasReply ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
            Proposed reply
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.25, mt: 0.5 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {task.ai_proposed_reply}
            </Typography>
          </Paper>
        </Box>
      ) : null}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          color="success"
          disabled={working}
          onClick={handleApprove}
          startIcon={<CheckCircleIcon />}
        >
          Approve & Execute
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={working}
          onClick={handleReject}
        >
          Reject
        </Button>
      </Stack>
    </Box>
  );
}

ProposalCard.propTypes = {
  task: PropTypes.object.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

function TaskTable({
  tasks,
  loading,
  sortConfig,
  selectedIds,
  onEdit,
  onToggleComplete,
  onDelete,
  onSortChange,
  onSelectionChange,
  onBulkDelete,
  onSnooze,
  onApproveProposal,
  onRejectProposal,
}) {
  const [expandedRows, setExpandedRows] = useState({});
  const lastClickedIndexRef = useRef(null);

  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);
  const allSelected = taskIds.length > 0 && taskIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && taskIds.some((id) => selectedIds.has(id));

  const toggleExpanded = (taskId) => {
    setExpandedRows((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const handleRowSelect = (taskId, index, event) => {
    const next = new Set(selectedIds);
    const isShift = event.nativeEvent && event.nativeEvent.shiftKey;
    const lastIndex = lastClickedIndexRef.current;

    if (isShift && lastIndex !== null && lastIndex !== undefined) {
      const start = Math.min(lastIndex, index);
      const end = Math.max(lastIndex, index);
      const shouldSelect = !next.has(taskId);
      for (let i = start; i <= end; i += 1) {
        const id = taskIds[i];
        if (id == null) continue;
        if (shouldSelect) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
    } else if (next.has(taskId)) {
      next.delete(taskId);
    } else {
      next.add(taskId);
    }
    lastClickedIndexRef.current = index;
    onSelectionChange(next);
  };

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(taskIds));
    }
    lastClickedIndexRef.current = null;
  };

  const expandedCount = useMemo(
    () => Object.values(expandedRows).filter(Boolean).length,
    [expandedRows],
  );

  const selectedCount = selectedIds.size;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Table view"
        subheader={
          selectedCount
            ? `${selectedCount} selected. Shift-click to extend the range.`
            : `Built for triage and detail review. ${expandedCount ? `${expandedCount} row${expandedCount === 1 ? '' : 's'} expanded.` : ''}`
        }
        action={
          selectedCount ? (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onSelectionChange(new Set())}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={onBulkDelete}
              >
                Delete {selectedCount}
              </Button>
            </Stack>
          ) : (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ minWidth: { md: 380 } }}>
              <TextField
                select
                size="small"
                label="Sort by"
                value={sortConfig.field}
                onChange={(event) => onSortChange(event.target.value, sortConfig.direction)}
              >
                {SORT_FIELD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Order"
                value={sortConfig.direction}
                onChange={(event) => onSortChange(sortConfig.field, event.target.value)}
              >
                {SORT_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )
        }
      />
      <CardContent>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tasks found. Try adjusting your filters or create a new task.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell padding="checkbox" />
                  <TableCell>Task</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Due date</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell padding="checkbox">State</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task, index) => {
                  const isExpanded = Boolean(expandedRows[task.id]);
                  const isSelected = selectedIds.has(task.id);
                  const subtaskCount = task.subtasks_count ?? (task.subtasks || []).length;
                  const hasProposal = Boolean(
                    (task.ai_proposed_actions && task.ai_proposed_actions.length) ||
                    (task.ai_proposed_reply || '').trim(),
                  );
                  const hasDetails = Boolean(
                    hasProposal ||
                    task.source_key ||
                    task.notes ||
                    task.description ||
                    subtaskCount,
                  );
                  const sourceBadge = task.source_domain ? SOURCE_BADGE[task.source_domain] : null;
                  const deepLink = deepLinkForTask(task);

                  return (
                    <Fragment key={task.id}>
                      <TableRow hover selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onClick={(event) => handleRowSelect(task.id, index, event)}
                            onChange={() => {}}
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(task.id)}
                            disabled={!hasDetails}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ minWidth: 280, maxWidth: 420 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {task.title}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            {hasProposal ? (
                              <Chip
                                size="small"
                                label={task.ai_confidence != null ? `🤖 ${task.ai_confidence}%` : '🤖 AI proposal'}
                                color="primary"
                              />
                            ) : null}
                            {subtaskCount ? (
                              <Chip size="small" label={`${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}`} variant="outlined" />
                            ) : null}
                          </Stack>
                          {task.notes ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} noWrap>
                              {task.notes}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ minWidth: 110 }}>
                          {sourceBadge ? (
                            <Chip
                              size="small"
                              label={sourceBadge.label}
                              color={sourceBadge.color}
                              variant="outlined"
                            />
                          ) : (
                            <Chip size="small" label="Manual" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>{task.client_name || '—'}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          {task.assignee_name ? (
                            <Chip size="small" color="primary" variant="outlined" label={task.assignee_name} />
                          ) : (
                            <Chip size="small" label="Unassigned" />
                          )}
                        </TableCell>
                        <TableCell>
                          {task.due_date ? dayjs(task.due_date).format('MMM D, YYYY') : '—'}
                        </TableCell>
                        <TableCell>
                          {task.created_at ? (
                            <Tooltip title={dayjs(task.created_at).format('MMM D, YYYY h:mm A')}>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {dayjs(task.created_at).fromNow()}
                              </Typography>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell>{PRIORITY_LABEL[task.priority] || task.priority_display || '—'}</TableCell>
                        <TableCell padding="checkbox">
                          {(() => {
                            const state = taskStateColor(task);
                            return (
                              <Tooltip title={state.label}>
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: state.color,
                                    mx: 'auto',
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={task.is_completed ? 'Reopen' : 'Mark done'}>
                            <IconButton
                              size="small"
                              onClick={() => onToggleComplete(task, !task.is_completed)}
                              sx={{ color: task.is_completed ? 'success.main' : 'text.secondary' }}
                            >
                              {task.is_completed ? (
                                <CheckCircleIcon fontSize="small" />
                              ) : (
                                <CheckCircleOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <SnoozeMenu task={task} onSnooze={onSnooze} />
                          {deepLink ? (
                            <Tooltip title={deepLink.label}>
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={deepLink.to}
                                sx={{ color: 'primary.main' }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Edit task">
                            <IconButton size="small" onClick={() => onEdit(task)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete task">
                            <IconButton size="small" onClick={() => onDelete(task)} sx={{ color: 'error.main' }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={11} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {(task.ai_proposed_actions && task.ai_proposed_actions.length) || (task.ai_proposed_reply || '').trim() ? (
                              <ProposalCard
                                task={task}
                                onApprove={onApproveProposal}
                                onReject={onRejectProposal}
                              />
                            ) : (
                              <Box sx={{ px: 2, py: 2.5, backgroundColor: (theme) => theme.palette.action.hover }}>
                                <Stack spacing={2.5}>
                                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                                    <Box sx={{ flex: 1 }}>
                                      <DetailBlock label="What needs to be done">
                                        {task.notes || task.description || 'No task notes available.'}
                                      </DetailBlock>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <DetailBlock label="Assignee">
                                        {task.assignee_name || 'Unassigned'}
                                      </DetailBlock>
                                      {task.source_key ? (
                                        <DetailBlock label="Source key">{task.source_key}</DetailBlock>
                                      ) : null}
                                    </Box>
                                  </Stack>
                                </Stack>
                              </Box>
                            )}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

TaskTable.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      source_key: PropTypes.string,
      source_domain: PropTypes.string,
      subtasks_count: PropTypes.number,
      snoozed_until: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  sortConfig: PropTypes.shape({
    field: PropTypes.oneOf(['created_at', 'due_date']).isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleComplete: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  onBulkDelete: PropTypes.func.isRequired,
  onSnooze: PropTypes.func.isRequired,
  onApproveProposal: PropTypes.func.isRequired,
  onRejectProposal: PropTypes.func.isRequired,
};

TaskTable.defaultProps = {
  tasks: [],
  loading: false,
};

export default TaskTable;
